<template>
  <div class="typeahead-wrap">

    <i class="fa fa-spinner fa-spin indicator" v-if="loading"></i>
    <template v-else>
      <i class="fa fa-search indicator" v-show="isEmpty"></i>
      <i class="fa fa-times indicator" v-show="isDirty" @click="reset"></i>
    </template>

    <div class="input-group">
      <input
        v-model="query"
        :ref="inputId"
        :id="inputId"
        :name="'input_' + name"
        type="text"
        class="typeahead-input form-control"
        v-bind="$attrs"
        :placeholder="placeholder"
        autocomplete="off"
        aria-autocomplete="none"

        @focus="onFocus"
        @blur="onBlur"
        @input="onUpdate"

        @keydown.down="onArrowDown"
        @keydown.up="onArrowUp"
        @keydown.enter.prevent="selectItem"
        @keydown.esc="reset"
      />

      <div class="input-group-append" v-if="relatedSrc">
        <button type="button" class="btn btn-default dropdown-toggle" data-toggle="dropdown" :title="relatedTitle">
          <i :class="relatedIcon"></i>
        </button>
        <div class="dropdown-menu">
          <ul class="related-items">
            <li v-for="(item, index) in relatedItems" @click="onSelectRelatedItem(item.email)"
                @mousemove="setActiveItem(index)">
              <slot name="item" :item="item">
                <span v-text="item"></span>
              </slot>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <ul v-show="showItems">
      <li v-for="(item, index) in items" :class="{active: selectedIndex === index}" @mousedown="selectItem" @mousemove="setActiveItem(index)">
        <slot name="item" :item="item">
          <span v-text="item"></span>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

export default {

  inheritAttrs: false,

  emits: ['update:modelValue', 'onSelect', 'onSelectRelatedItem', 'onDirectInput', 'onUpdate', 'onFocus', 'onBlur'],

  props: {
    // The selected item.
    modelValue: {
      type: [String, Number, Array, Object],
      default: null
    },
    // Input ID.
    id: {
      type: String,
      default: '',
    },
    // Input name.
    name: {
      type: String,
      default: '',
    },
    // The source url to get suggestions from.
    src: {
      type: String,
      default: '',
    },
    // The source url to get first related suggestions from.
    relatedSrc: {
      type: String,
      default: '',
    },
    // The title for the related items list.
    relatedTitle: {
      type: String,
      default: '',
    },
    // The icon for the related items list.
    relatedIcon: {
      type: String,
      default: '',
    },
    // Query parameter name.
    queryParamName: {
      type: String,
      default: 'q',
    },
    // The typeahead field placeholder.
    placeholder: {
      type: String,
      default: 'Start typing...',
    },
    // The minimum character length needed before triggering.
    minChars: {
      type: Number,
      default: 2,
    },
  },

  data () {
    return {
      // Current user query typed in typeahead field.
      query: '',

      // The suggestion items loaded from "src" based on user query.
      items: [],

      // The current selected item index.
      selectedIndex: -1,

      // The last selected item.
      selectedItem: null,

      // Indicates that items is loading now from "src".
      loading: false,

      // Indicates that input is currently in focus.
      inputFocused: false,

      // Input ID.
      inputId: this.id || 'typeahead_' + uuidv4(),

      // The suggestion related items loaded from "related src".
      relatedItems: [],
    }
  },

  computed: {
    // Indicates that matched items exist.
    hasItems() {
      return this.items.length > 0;
    },

    // Indicates that item list should be shown.
    showItems() {
      return this.hasItems && this.inputFocused;
    },

    // Indicates that currently the input is empty.
    isEmpty() {
      return this.query.trim().length === 0;
    },

    // Opposite to isEmpty.
    isDirty() {
      return !this.isEmpty;
    }
  },

  methods: {

    search() {
      if (this.query.length < this.minChars) {
        this.items = [];
        return;
      }
      this.getItems();
    },

    relatedSearch() {
      if (this.relatedSrc) {
        this.loading = true
        axios.get(this.relatedSrc)
          .then(response => {
            this.relatedItems = response.data;

            if (this.showItems && this.selectedIndex >= this.items.length) {
              this.selectedIndex = (this.items.length || 0) - 1;
            }
            if (this.items.length === 0) this.selectedIndex = -1;
          })
          .finally(() => {
            this.loading = false
          })
      }
    },

    reset() {
      this.query = '';
      this.items = [];
      this.selectedIndex = -1;
    },

    getItems() {
      this.loading = true
      let params = {};
      params[this.queryParamName] = this.query;
      axios.get(this.src, {'params': params})
        .then(response => {
          this.items = response.data;

          if (this.showItems && this.selectedIndex >= this.items.length) {
            this.selectedIndex = (this.items.length || 0) - 1;
          }
          if (this.items.length === 0) this.selectedIndex = -1;
        })
        .catch(error => {
          // @todo Handle errors.
        })
        .finally(() => {
          this.loading = false
        })
    },

    setActiveItem(index) {
      this.selectedIndex = index;
    },

    selectItem(e) {
      if (this.selectedIndex >= 0) {
        this.selectedItem = this.items[this.selectedIndex];
        this.$emit('onSelect', {
          item: this.selectedItem,
          query: this.query,
          items: this.items,
          event: e
        });
        this.$emit('update:modelValue', this.selectedItem);
      } else {
        this.$emit('onDirectInput', {
          query: this.query,
          items: this.items,
          event: e
        });
      }
      this.reset();

      this.$emit('onUpdate', {
        query: this.query,
        items: this.items,
        event: e
      });
    },

    onSelectRelatedItem(item) {
      this.$emit('onSelectRelatedItem', {
        query: item
      });
    },

    focus() {
      // SetTimeout here works better than $nextTick approach.
      setTimeout(() => { this.$refs[this.inputId].focus() }, 200)
    },

    onUpdate(e) {
      this.search();
      this.$emit('onUpdate', {
        query: this.query,
        items: this.items,
        event: e
      });
    },

    onFocus(e) {
      this.inputFocused = true;
      this.$emit('onFocus', {
        query: this.query,
        items: this.items,
        event: e
      });
    },

    onBlur(e) {
      this.inputFocused = false;
      this.$emit('onBlur', {
        query: this.query,
        items: this.items,
        event: e
      });
    },

    onArrowDown() {
      if (this.showItems && this.selectedIndex < this.items.length - 1) {
        this.selectedIndex++;
      }
    },

    onArrowUp() {
      if (this.showItems && this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    },
  },

  mounted() {
    if (this.relatedSrc) {
      this.relatedSearch();
    }
  }
}
</script>

<style scoped>
.typeahead-wrap {
  position: relative;
}
.typeahead-input:focus {
  border-color: #4f8dc0;
  outline: 0;
  box-shadow: inset 0 1px 1px rgba(0,0,0,.075),0 0 8px #4f8dc0;
}
.fa-times {
  cursor: pointer;
}
i.indicator {
  position: absolute;
  top: 12px;
  right: 10px;
  opacity: 0.4;
}

ul {
  position: absolute;
  padding: 0;
  margin-top: 4px;
  min-width: 100%;
  background-color: #fff;
  list-style: none;
  border-radius: 4px;
  box-shadow: 0 0 10px rgba(0,0,0, 0.25);
  z-index: 1000;
}
li {
  padding: 10px 16px;
  border-bottom: 1px solid #ccc;
  cursor: pointer;
}
li:first-child {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
li:last-child {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  border-bottom: 0;
}
li > span {
  display: block;
  color: #2c3e50;
}
.active {
  background-color: #1c84c6;
  color: white;
}
.active > span, .active > small {
  color: white;
}
.related-items li:hover {
  background-color: #1c84c6;
  color: white;
}
</style>
